import React from "react";
import { Flame, Anchor, FileText, Download, Droplet, Gauge, ShieldCheck, Truck, Globe, Phone, MapPin, Users } from "lucide-react";

const IMG_OIL = "https://images.unsplash.com/photo-1572093241294-2cb147be4281?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxvaWwlMjByaWclMjBzdW5zZXQlMjBkYXJrJTIwc2lsaG91ZXR0ZXxlbnwwfHx8fDE3NzY2NzYwMDR8MA&ixlib=rb-4.1.0&q=85";

export default function OilPage({ onDownload, documents }) {
  const fcoDoc = documents?.find((d) => d.id === "doc-fco");
  const ncndaDoc = documents?.find((d) => d.id === "doc-ncnda");
  const specDoc = documents?.find((d) => d.id === "doc-spec-en590");
  const cpDoc = documents?.find((d) => d.id === "doc-cp-millpond");

  return (
    <div data-testid="view-oil" className="max-w-5xl">
      <Hero />

      <Section eyebrow="Produits" title="Les carburants négociés sur le marché mondial" index="01">
        <p>
          Le marché pétrolier est un océan de codes et de normes. Voici les produits les plus traités en intermédiation —
          ceux que vous verrez dans 95 % des SCO et FCO.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
          {[
            { code: "EN590 10ppm", name: "Diesel ULSD européen", spec: "Soufre ≤ 10 ppm · Cétane ≥ 51 · Densité 0,820–0,845", use: "Transport routier, chauffage. Standard UE (AFNOR / EN590).", hot: true },
            { code: "D2 / GOST 305-82", name: "Diesel russe (Gasoil)", spec: "Soufre 500–2000 ppm · Cétane 45 · Densité 0,830", use: "Marchés CIS, Afrique, Asie du Sud-Est. Souvent confondu avec EN590.", hot: false },
            { code: "Jet Fuel A1", name: "Kérosène aviation", spec: "Spec DEF STAN 91-91 · Point de congélation ≤ −47°C · Soufre ≤ 0,3 %", use: "Aviation civile internationale. Livré en hubs : Rotterdam, Fujairah, Singapour.", hot: true },
            { code: "JP54", name: "Colonial Grade 54", spec: "Kérosène non-traité · précurseur Jet A1", use: "Populaire en offres TTO. À traiter pour atteindre spec aviation.", hot: false },
            { code: "Mazut M100", name: "Fuel oil résiduel", spec: "Soufre 1–3,5 % · Densité 0,890–0,950 · Viscosité élevée", use: "Centrales, marine bunker, industrie lourde. Marché spot très actif.", hot: false },
            { code: "IFO 380 / VLSFO", name: "Marine bunker fuel", spec: "ISO 8217 · IMO 2020 : soufre ≤ 0,5 %", use: "Soutage maritime mondial. Hubs : Singapour, Fujairah, Rotterdam.", hot: false },
            { code: "LNG", name: "Liquefied Natural Gas", spec: "Méthane 85–99 % · −162°C · Densité 0,41–0,5", use: "Méthaniers dédiés. Contrats long-terme indexés Brent ou TTF.", hot: true },
            { code: "LPG", name: "Butane / Propane", spec: "Mix propane-butane · pression 8–18 bar", use: "Domestique, industrie, carburant automobile (autogaz).", hot: false },
            { code: "REBCO / Urals", name: "Brut russe d'export", spec: "API 31–32° · Soufre 1,2–1,3 %", use: "Benchmark export russe. Décotes/primes vs Brent selon sanctions.", hot: false },
            { code: "AGO", name: "Automotive Gas Oil", spec: "Soufre 500–3000 ppm · Densité 0,830", use: "Marché africain : Nigeria, Ghana, Togo. Moins raffiné que EN590.", hot: false },
          ].map((p) => (
            <div key={p.code} className={`border p-6 transition-colors ${p.hot ? "border-[#D4AF37]/40 bg-[#141414]" : "border-white/10 bg-[#0c0c0c] hover:border-white/25"}`}>
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-[#D4AF37]">{p.code}</span>
                {p.hot && <span className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">High demand</span>}
              </div>
              <div className="font-serif text-lg mt-2">{p.name}</div>
              <div className="text-xs text-[#A1A1AA] mt-3 font-mono leading-relaxed">{p.spec}</div>
              <p className="text-sm text-[#C4C4C7] mt-3">{p.use}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Fiche technique" title="Spec Sheet ULSD EN590 10 ppm (référence marché)" index="02">
        <p>
          Voici la <strong className="text-white">spec sheet standard</strong> que vous verrez passer dans 9 FCO
          sur 10 concernant le diesel européen. À connaître par cœur : un acheteur sérieux exige toujours ces
          22 paramètres certifiés par SGS/ITS avant de libérer la SBLC.
        </p>
        <div className="mt-10 border border-white/10 overflow-hidden">
          <div className="px-6 py-4 bg-[#141414] border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Specification</div>
              <div className="font-serif text-lg mt-1">ULSD 10 ppm Diesel Fuel — EN 590</div>
            </div>
            {specDoc && (
              <button
                onClick={() => onDownload(specDoc)}
                data-testid="oil-download-doc-spec-en590"
                className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-[10px] uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                <Download className="h-3 w-3" /> Fiche complète
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] border-b border-white/10">
                  <th className="px-5 py-3">Property</th>
                  <th className="px-5 py-3">Test method</th>
                  <th className="px-5 py-3">Unit</th>
                  <th className="px-5 py-3">Guarantee</th>
                  <th className="px-5 py-3">Limit</th>
                </tr>
              </thead>
              <tbody className="font-mono text-[13px] text-[#E4E4E7]">
                {[
                  ["Density at 15°C", "—", "kg/m³", "—", "820–845"],
                  ["Polycyclic aromatic hydrocarbons", "EN 12916", "wt%", "B", "Max"],
                  ["Flash Point", "EN 2719", "°C", ">55", "—"],
                  ["Cold Filter Plugging Point (CFPP)", "EN 116", "°C", "—", "—"],
                  ["Winter Grade", "—", "°C", "−15", "max"],
                  ["Summer Grade", "—", "°C", "5", "max"],
                  ["Distillation", "EN ISO 3405", "—", "—", "—"],
                  ["  · Recovered at 250°C", "—", "Vol%", "65", "max"],
                  ["  · Recovered at 350°C", "—", "Vol%", "85", "min"],
                  ["  · 95% (Vol/Vol) Recovered at", "—", "°C", "360", "max"],
                  ["Sulphur", "EN ISO 20846 / 20884", "mg/kg", "10", "max"],
                  ["Carbon Residue (on 10% residue)", "EN ISO 10370", "wt%", "0,30", "max"],
                  ["Viscosity at 40°C", "EN ISO 3104", "cSt", "2,0 – 4,5", "—"],
                  ["Copper Strip Corrosion (3h @ 50°C)", "EN ISO 2160", "rating", "No. 1", "max"],
                  ["Fatty Acid Methyl Ester (FAME)", "EN 14078", "Vol%", "7,0", "max"],
                  ["Cetane Number", "EN ISO 5165 / EN 15195", "—", "51", "min"],
                  ["Cetane Index (calculated)", "EN ISO 4264", "—", "46", "min"],
                  ["Water", "EN ISO 12937", "mg/kg", "200", "max"],
                  ["Particulate Matter", "EN 12662", "mg/kg", "24", "max"],
                  ["Oxidation Stability", "EN ISO 12205 / 15751", "g/m³·h", "25 / 20", "max / min"],
                  ["Lubricity (wsd 1,4) at 60°C", "EN ISO 12156/1", "µm", "450", "max"],
                ].map((row, i) => {
                  const isSub = row[0].startsWith("  ·");
                  return (
                    <tr key={i} className={`border-b border-white/5 last:border-b-0 ${i % 2 === 0 ? "bg-[#0c0c0c]" : "bg-[#0a0a0a]"} hover:bg-[#141414] transition-colors`}>
                      <td className={`px-5 py-3 ${isSub ? "text-[#A1A1AA] pl-8" : "text-white"}`}>{row[0].replace("  · ", "")}</td>
                      <td className="px-5 py-3 text-[#A1A1AA] text-xs">{row[1]}</td>
                      <td className="px-5 py-3 text-[#A1A1AA]">{row[2]}</td>
                      <td className="px-5 py-3 text-[#D4AF37]">{row[3]}</td>
                      <td className="px-5 py-3 text-[#A1A1AA] text-xs">{row[4]}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <Callout icon={ShieldCheck} title="Comment l'intermédiaire lit cette spec sheet">
          3 paramètres à vérifier impérativement sur chaque offre :
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li><strong className="text-white">Sulphur ≤ 10 mg/kg</strong> — si l'offre mentionne "500 ppm" ou plus, ce n'est PAS de l'EN590 (c'est du D2 ou AGO).</li>
            <li><strong className="text-white">Cetane Number ≥ 51</strong> — en dessous = produit non-conforme aux moteurs européens modernes.</li>
            <li><strong className="text-white">Density 820–845 kg/m³</strong> — hors plage = contamination ou autre produit rebaptisé.</li>
          </ul>
          Les offres "EN590" à prix cassé présentent souvent des specs hors norme. Exigez le rapport SGS/ITS avant toute discussion.
        </Callout>
      </Section>

      <Section eyebrow="Ports & hubs" title="Où se livre physiquement le pétrole" index="03">
        <p>
          Un deal pétrolier se cadre toujours autour d'un port de chargement (load port) et d'un port de déchargement
          (discharge port). Les hubs mondiaux concentrent <strong className="text-white">80 % des volumes</strong>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {[
            { zone: "Europe", ports: ["Rotterdam (ARA)", "Antwerp", "Amsterdam", "Hamburg", "Trieste"], role: "Hub mondial produits raffinés" },
            { zone: "Golfe Persique", ports: ["Fujairah (UAE)", "Jebel Ali", "Bandar Abbas", "Ras Tanura", "Jubail"], role: "Centre mondial bunker + brut" },
            { zone: "Asie", ports: ["Singapore (Jurong)", "Ulsan", "Yokohama", "Shanghai (Yangshan)"], role: "Hub Asie-Pacifique" },
            { zone: "USA", ports: ["Houston", "New Orleans", "Corpus Christi", "NY Harbor"], role: "Plus grand complexe raffinage mondial" },
            { zone: "Afrique", ports: ["Lagos (Apapa)", "Lomé", "Abidjan", "Durban", "Dar es Salaam"], role: "Import carburants Afrique" },
            { zone: "Russie / Mer Noire", ports: ["Primorsk", "Ust-Luga", "Novorossiysk", "Tuapse"], role: "Export brut et diesel russes" },
          ].map((z) => (
            <div key={z.zone} className="border border-white/10 bg-[#0c0c0c] p-6">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
                <Anchor className="h-3.5 w-3.5" /> {z.zone}
              </div>
              <ul className="mt-4 space-y-1.5 text-sm text-white font-mono">
                {z.ports.map((p) => <li key={p}>· {p}</li>)}
              </ul>
              <p className="mt-4 pt-4 border-t border-white/10 text-xs text-[#A1A1AA]">{z.role}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Procédures de transfert" title="TTO, TTT, TTV, VTV : le vocabulaire du transfert physique" index="04">
        <p>
          Les "transferts" décrivent <strong className="text-white">comment</strong> la cargaison change de main.
          Chaque code implique une procédure documentaire et un niveau de risque différents.
        </p>
        <div className="mt-8 space-y-3">
          {[
            { code: "TTO", name: "Tanker Take Over", risk: "Élevé", detail: "L'acheteur reprend directement le navire-citerne du vendeur, en mer. Rare — utilisé pour cargo déjà financé. Nécessite SGS/ITS inspection avant transfert titre." },
            { code: "TTT", name: "Tank-to-Tank", risk: "Moyen", detail: "Transfert de produit entre deux tanks en terminal (ex : Fujairah). Le vendeur transfère la propriété dans le tank de l'acheteur après paiement. Ultra-sécurisé avec SGS Q&Q." },
            { code: "TTV", name: "Tank-to-Vessel", risk: "Faible", detail: "Le plus classique. Le produit est chargé depuis le tank du vendeur vers le navire de l'acheteur. Bill of Lading = titre de propriété." },
            { code: "VTV", name: "Vessel-to-Vessel (ship-to-ship)", risk: "Moyen", detail: "Transbordement en mer entre deux navires. Utilisé pour contournement de sanctions ou optimisation logistique. Nécessite autorisation du port." },
            { code: "CI Dip & Pay", name: "CI Dip Test and Pay", risk: "Très faible", detail: "L'acheteur envoie son inspecteur (SGS/ITS) vérifier qualité et quantité dans le tank du vendeur. Paiement déclenché après dip test positif. Référence industrie." },
          ].map((t) => (
            <div key={t.code} className="border border-white/10 bg-[#0c0c0c] p-6 hover:border-white/25 transition-colors">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-[#D4AF37] text-lg">{t.code}</span>
                  <span className="font-serif text-lg">{t.name}</span>
                </div>
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#A1A1AA]">Risque : {t.risk}</span>
              </div>
              <p className="text-sm text-[#A1A1AA] mt-3">{t.detail}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Incoterms" title="FOB vs CIF : quelle structure choisir ?" index="04">
        <p>
          Sur le pétrole, deux Incoterms dominent : <strong className="text-white">FOB</strong> (vendeur livre au port de
          chargement) et <strong className="text-white">CIF</strong> (vendeur paie le fret + assurance jusqu'au port de
          destination).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <DealCard
            code="FOB"
            title="Free On Board"
            bullets={[
              "Vendeur : met le produit à bord du navire affrété par l'acheteur",
              "Transfert du risque : passé le bastingage du navire",
              "Acheteur : paie le fret, l'assurance, les frais de destination",
              "Idéal si l'acheteur a ses propres navires / contrats de fret",
            ]}
            example="Ex : FOB Rotterdam EN590 — l'acheteur envoie son navire se charger à Rotterdam."
          />
          <DealCard
            code="CIF"
            title="Cost, Insurance, Freight"
            bullets={[
              "Vendeur : inclut coût produit + fret maritime + assurance",
              "Livraison au port de destination convenu (ASWP = Any Safe World Port)",
              "Acheteur : dédouane à l'arrivée, prend possession",
              "Plus simple pour l'acheteur, plus cher (marge fret 3–8 %)",
            ]}
            example="Ex : CIF ASWP — vendeur livre où l'acheteur veut, monde entier."
          />
        </div>
      </Section>

      <Section eyebrow="Pétrodollar" title="L'extraterritorialité du dollar : une loi qui s'impose au monde" index="06">
        <p>
          Depuis l'accord Nixon-Arabie saoudite de 1974, <strong className="text-white">toutes les transactions pétrolières
          internationales se règlent en dollars américains</strong>. Ce n'est pas une coutume — c'est un cadre juridique
          imposé unilatéralement par les États-Unis via la doctrine dite de l'<em className="text-white">extraterritorialité</em>
          du dollar.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c] p-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Le principe</div>
            <div className="font-serif text-xl text-white mt-2">Toute transaction en USD relève du droit américain</div>
            <p className="mt-5 text-sm text-[#A1A1AA] leading-relaxed">
              Un paiement en dollars transite obligatoirement par le système de compensation interbancaire américain
              (CHIPS / Fedwire à New York). Dès qu'un dollar passe par New York — <em>même pour une transaction entre
              un vendeur saoudien et un acheteur chinois</em> — le Département du Trésor US (OFAC) et la Justice
              fédérale s'arrogent juridiction.
            </p>
          </div>
          <div className="border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">La sanction</div>
            <div className="font-serif text-xl text-white mt-2">Contourner le USD = crime fédéral</div>
            <p className="mt-5 text-sm text-[#A1A1AA] leading-relaxed">
              Toute tentative de structurer une transaction pétrolière en yuan, euro, rouble ou dirham pour échapper
              au contrôle américain peut être qualifiée de <strong className="text-white">violation de sanctions</strong>
              ou de blanchiment. BNP Paribas (2014) a payé <strong className="text-white">8,9 Md $</strong> de pénalité
              au DOJ américain. Standard Chartered, HSBC, Credit Suisse, Société Générale — la liste est longue.
            </p>
          </div>
        </div>

        <Callout icon={ShieldCheck} title="Ce que ça signifie concrètement pour l'intermédiaire">
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li><strong className="text-white">Toutes les SBLC, DLC, MT103, MT760</strong> que vous verrez passer sur du pétrole sont libellées en USD. Point.</li>
            <li><strong className="text-white">Vérifiez les sanctions OFAC</strong> (SDN list) sur chaque contrepartie — acheteur, vendeur, banque émettrice, banque correspondante, armateur. Une seule entité listée contamine toute la chaîne.</li>
            <li><strong className="text-white">Un deal sur l'Iran, le Venezuela, la Russie ou la Syrie</strong> en dollars = exposition pénale directe, même si vous n'êtes pas citoyen américain, même si vous n'avez jamais mis les pieds aux USA.</li>
            <li><strong className="text-white">Les alternatives non-USD existent</strong> (yuan sur Shanghai INE, INSTEX en Europe, rouble sur SPIMEX) mais restent marginales — &lt;3 % du marché mondial — et excluent la plupart des acheteurs institutionnels.</li>
          </ul>
        </Callout>

        <p className="mt-8 text-sm text-[#A1A1AA]">
          <strong className="text-white">Résumé pratique :</strong> tant que vous opérez sur des flux pétroliers
          classiques (EN590, Jet A1, D2, brut WTI/Brent), vous êtes de facto dans le droit américain.
          Un banquier correspondant US peut geler un MT760 en 24h sur simple soupçon de non-conformité OFAC.
          <em className="text-[#D4AF37]"> C'est aussi pour ça que les vrais traders dépensent une fortune en compliance</em>
          — et pourquoi tout intermédiaire sérieux exige KYC/KYB complet avant d'engager sa réputation sur un deal.
        </p>
      </Section>

      <Section eyebrow="Paiement" title="SBLC, DLC, MT103 et les pièges classiques" index="07">
        <p>
          Les mêmes instruments SWIFT que sur l'or, mais appliqués à des volumes autrement plus importants (1 à 500 M$ par cargo).
          Les arnaques sont nombreuses — <strong className="text-white">connaître la séquence tue 90 % des pertes de temps</strong>.
        </p>
        <div className="mt-10 space-y-3">
          {[
            { code: "MT199/MT799", label: "Pre-advice", detail: "Message non-financier. L'acheteur annonce qu'il a les fonds et les procédures. Obligatoire avant tout MT760.", risk: "Aucun signal financier" },
            { code: "MT760", label: "SBLC / DLC irrévocable", detail: "Garantie bancaire opérationnelle. 2 % frais bancaires vendeur. Utilisé pour tous les deals >5 M$. Formats UCP 600 ou ISP 98.", risk: "Très faible — audit bancaire requis" },
            { code: "MT700", label: "Documentary LC (DLC)", detail: "Crédit documentaire : paiement contre présentation de BL + SGS + CO + Cargo Manifest. Procédure cadrée par UCP 600 ICC.", risk: "Faible" },
            { code: "MT103", label: "TT Wire — paiement solde", detail: "Virement classique. Utilisé pour payer 2 % de dépôt ou le solde après inspection/dip test.", risk: "Faible contre POP vérifié" },
            { code: "MT103/23", label: "Conditional MT103", detail: "Virement conditionnel — débloqué contre POP. Alternative au SBLC pour deals 500 k–5 M$.", risk: "Moyen — audit clauses" },
          ].map((p) => (
            <div key={p.code} className="border border-white/10 bg-[#0c0c0c] p-6 hover:border-white/25 transition-colors">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-[#D4AF37] text-lg">{p.code}</span>
                  <span className="font-serif text-lg">{p.label}</span>
                </div>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#A1A1AA]">{p.risk}</span>
              </div>
              <p className="text-sm text-[#A1A1AA] mt-3">{p.detail}</p>
            </div>
          ))}
        </div>

        <Callout icon={ShieldCheck} title="Séquence professionnelle d'un deal pétrole">
          <ol className="list-decimal pl-5 space-y-2 mt-2">
            <li>NCNDA multi-parties signé (broker chain IMFPA inclus).</li>
            <li>ICPO (Irrevocable Corporate Purchase Order) de l'acheteur.</li>
            <li>SCO → FCO du vendeur (conditions complètes : prix, quantités, livraison, paiement).</li>
            <li>Company Profile + Product Spec Sheet + Commission Agreement.</li>
            <li>Acheteur émet MT799 / Soft POF → Vendeur émet POP (ATSC, Q88, SGS report).</li>
            <li>Acheteur émet MT760 SBLC opérationnelle → Vendeur commence chargement.</li>
            <li>Dip test SGS/ITS au port de destination → MT103 solde + commission IMFPA.</li>
          </ol>
        </Callout>
      </Section>

      <Section eyebrow="Ressources" title="Documents pour un deal pétrole" index="08">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DocRef doc={fcoDoc} onDownload={onDownload} accent="FCO — Exemple réel Pétrole" note="Millpond Petroleum — Full Corporate Offer EN590 Jebel Ali. Structure complète de référence." />
          <DocRef doc={cpDoc} onDownload={onDownload} accent="Company Profile — Millpond" note="Présentation société vendeur : structure, licences, track record. À joindre systématiquement." />
          <DocRef doc={specDoc} onDownload={onDownload} accent="Spec Sheet — EN590 10 ppm" note="Product Spec Sheet ULSD Diesel Fuel — 22 paramètres certifiés SGS. À joindre à toute offre EN590." />
          <DocRef doc={ncndaDoc} onDownload={onDownload} accent="NCNDA — Chaîne de brokers" note="Protège votre commission dans un deal multi-intermédiaires. Signature avant tout échange." />
        </div>
        <p className="mt-6 text-xs text-[#52525B]">
          Documents complémentaires à joindre : Commission Agreement (IMFPA), SPA (Sales & Purchase Agreement),
          Q88 (fiche navire), ATSC (Authority to Sell and Collect).
        </p>
      </Section>

      <Section eyebrow="Rolodex" title="Fournisseurs & traders pétroliers" index="09">
        <p>
          Les <strong className="text-white">acteurs majeurs du marché pétrolier mondial</strong> — traders indépendants suisses,
          producteurs nationaux du Golfe, distributeurs africains. WhatsApp ou téléphone direct pour contact.
        </p>

        <h3 className="mt-10 mb-5 font-serif text-lg text-white flex items-center gap-3">
          <span className="h-px w-8 bg-[#D4AF37]" /> Traders indépendants — Genève / Suisse
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <OilSupplier name="Vitol Group" tag="Plus grand trader pétrolier indépendant" location="Genève · Rotterdam · Singapour" website="https://www.vitol.com" phone="+41 22 322 11 11" />
          <OilSupplier name="Trafigura" tag="Brut, raffinés, métaux énergétiques" location="Genève · Singapour" website="https://www.trafigura.com" phone="+41 22 592 4646" />
          <OilSupplier name="Glencore Energy" tag="Pétrole brut, gaz, charbon" location="Baar, Suisse" website="https://www.glencore.com" phone="+41 41 709 2000" />
          <OilSupplier name="Gunvor Group" tag="Brut russe, raffinés, LNG" location="Genève" website="https://gunvorgroup.com" phone="+41 22 906 19 00" />
          <OilSupplier name="Mercuria Energy Trading" tag="Multi-produits — brut, raffinés, biocarburants" location="Genève" website="https://mercuria.com" phone="+41 22 594 84 00" />
          <OilSupplier name="Litasco SA (Lukoil)" tag="Bras trading international Lukoil" location="Genève" website="https://www.litasco.com" phone="+41 22 819 75 00" />
        </div>

        <h3 className="mt-12 mb-5 font-serif text-lg text-white flex items-center gap-3">
          <span className="h-px w-8 bg-[#D4AF37]" /> Producteurs nationaux — Golfe
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <OilSupplier name="ENOC (Emirates National Oil)" tag="Producteur intégré UAE — raffinés" location="Dubai, UAE" website="https://www.enoc.com" phone="+971 4 337 4400" />
          <OilSupplier name="ADNOC Trading" tag="Murban crude, raffinés" location="Abu Dhabi, UAE" website="https://www.adnoc.ae" phone="+971 2 707 0000" />
          <OilSupplier name="Saudi Aramco Trading" tag="Arab Light / Medium / Heavy" location="Dhahran, KSA" website="https://www.aramcotrading.com" phone="+966 13 873 5000" />
        </div>

        <h3 className="mt-12 mb-5 font-serif text-lg text-white flex items-center gap-3">
          <span className="h-px w-8 bg-[#D4AF37]" /> Afrique & Moyen-Orient — distribution
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <OilSupplier name="BB Energy" tag="Trader Moyen-Orient & Afrique" location="Londres · Dubai" website="https://www.bb-energy.com" phone="+44 20 7016 7700" />
          <OilSupplier name="Sahara Group" tag="Bonny Light, gasoil, fuel oil" location="Lagos · Dubai" website="https://www.sahara-group.com" phone="+234 1 279 3811" />
          <OilSupplier name="Oryx Energies" tag="Distribution downstream Afrique de l'Ouest" location="Genève · Dakar" website="https://www.oryxenergies.com" phone="+41 22 718 02 02" />
        </div>
      </Section>
    </div>
  );
}

// ---------- Subcomponents ----------
const Hero = () => (
  <section className="relative mb-16 border border-white/10 bg-[#0c0c0c] overflow-hidden">
    <img src={IMG_OIL} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
    <div className="relative p-10 md:p-14">
      <div className="flex items-center gap-3">
        <Flame className="h-5 w-5 text-[#D4AF37]" />
        <span className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">Secteur · 02</span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight mt-6 max-w-3xl">
        Le <em className="italic text-[#F5F5DC]">Pétrole</em> — EN590, Jet A1, <span className="text-[#D4AF37]">LNG</span>.
      </h1>
      <p className="mt-6 max-w-2xl text-[#A1A1AA] leading-relaxed">
        Produits, hubs portuaires, procédures TTT/TTV, Incoterms FOB/CIF, instruments bancaires : cadrer un deal cargo
        de A à Z.
      </p>
    </div>
  </section>
);

const Section = ({ eyebrow, title, index, children }) => (
  <section className="mb-20">
    <div className="flex items-baseline gap-6 mb-8">
      <span className="font-serif text-xl text-[#D4AF37] tabular-nums">{index}</span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{eyebrow}</div>
        <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-light mt-2 leading-tight">{title}</h2>
      </div>
    </div>
    <div className="text-[#C4C4C7] leading-relaxed space-y-4 pl-0 md:pl-10">{children}</div>
  </section>
);

const Callout = ({ icon: Icon, title, children }) => (
  <div className="mt-10 border-l-2 border-[#D4AF37] bg-[#141414] p-6 md:p-8">
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{title}</span>
    </div>
    <div className="mt-4 text-[#E4E4E7] leading-relaxed text-sm">{children}</div>
  </div>
);

const DealCard = ({ code, title, bullets, example }) => (
  <div className="border border-white/10 bg-[#0c0c0c] p-7">
    <div className="flex items-center gap-3">
      <Truck className="h-4 w-4 text-[#D4AF37]" />
      <span className="font-mono text-[#D4AF37] text-xl">{code}</span>
    </div>
    <div className="font-serif text-2xl mt-2">{title}</div>
    <ul className="mt-6 space-y-2 text-sm text-[#A1A1AA]">
      {bullets.map((b, i) => <li key={i} className="flex gap-2"><span className="text-[#D4AF37]">·</span><span>{b}</span></li>)}
    </ul>
    <p className="mt-6 pt-5 border-t border-white/10 text-xs text-[#52525B] italic">{example}</p>
  </div>
);

const DocRef = ({ doc, onDownload, accent, note }) => {
  if (!doc) return null;
  return (
    <div className="border border-white/10 bg-[#0c0c0c] p-6 group hover:border-[#D4AF37]/30 transition-colors">
      <div className="flex items-center gap-3">
        <FileText className="h-4 w-4 text-[#D4AF37]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{accent}</span>
      </div>
      <div className="font-serif text-xl mt-3">{doc.name}</div>
      <p className="text-sm text-[#A1A1AA] mt-3">{note}</p>
      <button
        onClick={() => onDownload(doc)}
        data-testid={`oil-download-${doc.id}`}
        className="mt-5 inline-flex items-center gap-2 px-5 py-3 border border-white/15 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
      >
        <Download className="h-3.5 w-3.5" /> Télécharger PDF
      </button>
    </div>
  );
};

const OilSupplier = ({ name, tag, location, website, phone }) => (
  <div
    data-testid={`oil-supplier-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="border border-white/10 bg-[#0c0c0c] p-6 md:p-7 hover:border-[#D4AF37]/50 transition-colors flex flex-col"
  >
    <div className="flex items-center gap-3">
      <Users className="h-4 w-4 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{tag}</span>
    </div>
    <h3 className="font-serif text-xl mt-3 text-white">{name}</h3>
    {location && (
      <div className="mt-2 flex items-center gap-2 text-xs text-[#A1A1AA]">
        <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" />
        <span>{location}</span>
      </div>
    )}
    <div className="mt-5 space-y-2.5 text-sm">
      {website && (
        <a
          href={website}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors break-all"
        >
          <Globe className="h-4 w-4 shrink-0 text-[#D4AF37]" />
          <span className="truncate">{website.replace(/^https?:\/\//, "")}</span>
        </a>
      )}
      {phone && (
        <a
          href={`tel:${phone.replace(/\s/g, "")}`}
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
        >
          <Phone className="h-4 w-4 shrink-0 text-[#D4AF37]" />
          <span className="font-mono tracking-tight">{phone}</span>
        </a>
      )}
    </div>
  </div>
);
